import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import toast from 'react-hot-toast';
import { useAppSelector } from 'app/hooks';
import flagIssueService, { FLAG_ISSUE_TYPE_OPTIONS } from 'services/flagIssueService';
import type { FlagIssueType } from 'services/flagIssueService';

const MAX_LEN = 500;

const FlagIssueButton: React.FC = () => {
  const personalStore = useAppSelector((store) => store.basicInformation.myBasicInformation);

  const [open, setOpen] = useState(false);
  const [issueType, setIssueType] = useState<FlagIssueType | ''>('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const userName = [personalStore?.first_name, personalStore?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!message.trim() || !issueType) return;
    setSubmitting(true);
    try {
      await flagIssueService.submitFlag({
        issue_type: issueType,
        message: message.trim(),
        surface: 'GradMap',
        page_url: typeof window !== 'undefined' ? window.location.href : undefined,
        user_name: userName || undefined,
      });
      toast.success("Thanks for flagging this. We'll look into it and follow up if we need more info.");
      setOpen(false);
      setIssueType('');
      setMessage('');
    } catch (err) {
      // http interceptor already surfaces an error toast; keep a fallback.
      toast.error('Unable to submit your report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Flag an Issue">
        <IconButton
          aria-label="Flag an Issue"
          onClick={() => setOpen(true)}
          size="large"
          sx={{
            color: '#6b7280',
            '&:hover': { color: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.08)' },
          }}>
          <FlagOutlinedIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle className="fw-bold">Flag an Issue</DialogTitle>
        <DialogContent>
          <div className="d-flex flex-column gap-3 pt-2">
            <div>
              <label className="form-label fw-semibold mb-3">What kind of issue is this?</label>
              <TextField
                select
                fullWidth
                size="small"
                value={issueType}
                onChange={(e) => setIssueType(e.target.value as FlagIssueType)}
                SelectProps={{ displayEmpty: true }}>
                <MenuItem value="" disabled>
                  Select an issue type
                </MenuItem>
                {FLAG_ISSUE_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <div>
              <label className="form-label fw-semibold mb-3">What&apos;s wrong?</label>
              <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder="Describe the issue so we can look into it."
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
                helperText={`${message.length}/${MAX_LEN}`}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!message.trim() || !issueType || submitting}>
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FlagIssueButton;
